/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileText } from 'lucide-react';
import { useUpdateJrxml } from '@/hooks/api/useTemplateCatalog';
import { useNotification } from '@/hooks/useNotification';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templateId: string | null;
  templateCode: string;
}

export default function ReUploadJrxmlModal({ isOpen, onClose, templateId, templateCode }: Props) {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();
  const updateJrxmlMutation = useUpdateJrxml();

  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isOpen || !templateId) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showError(t('templateCatalog.reupload.fileRequired'));
      return;
    }

    const formData = new FormData();
    formData.append('jrxml', file);

    try {
      await updateJrxmlMutation.mutateAsync({ id: templateId, data: formData });
      success(t('templateCatalog.reupload.success'));
      setFile(null);
      onClose();
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.reupload.error'));
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t('templateCatalog.reupload.title')}: {templateCode}</h2>
          <button onClick={onClose} className="modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: 0 }}>
            {t('templateCatalog.reupload.description')}
          </p>

          <div className="form-group">
            <div className="dropzone" onClick={() => inputRef.current?.click()}>
              <FileText size={24} />
              {file ? (
                <span className="dropzone-filename">{file.name}</span>
              ) : (
                <span>{t('templateCatalog.upload.selectJrxml')}</span>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".jrxml"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={updateJrxmlMutation.isPending || !file}>
              <Upload size={14} />
              {updateJrxmlMutation.isPending ? t('common.uploading') : t('templateCatalog.reupload.submit')}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); z-index: 1000; }
        .modal-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 90%; max-width: 480px; background: var(--color-bg-secondary); border: 1px solid var(--color-border-default); border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.3); z-index: 1001; }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--color-border-subtle); }
        .modal-header h2 { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
        .modal-close { background: none; border: none; color: var(--color-text-muted); cursor: pointer; padding: 4px; border-radius: 6px; }
        .modal-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
        .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
        .modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding-top: 12px; border-top: 1px solid var(--color-border-subtle); }
        .form-group { display: flex; flex-direction: column; gap: 6px; }
        .dropzone { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px; border: 2px dashed var(--color-border-default); border-radius: 8px; cursor: pointer; color: var(--color-text-muted); font-size: 12px; transition: all 0.15s; }
        .dropzone:hover { border-color: var(--color-brand); color: var(--color-text-secondary); }
        .dropzone-filename { color: var(--color-text-primary); font-weight: 500; }
        .btn-primary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: var(--color-brand); color: white; border: none; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { padding: 8px 16px; background: transparent; color: var(--color-text-secondary); border: 1px solid var(--color-border-default); border-radius: 8px; font-size: 13px; cursor: pointer; }
        .btn-secondary:hover { background: var(--color-bg-hover); }
      `}</style>
    </>
  );
}
